import { useEffect, useMemo, useState } from 'react';
import { fetchRoles } from 'services/api/roles';
import { RoleListRow } from 'types/api/roles';

/**
 * Every role, keyed by id — the roles list as a lookup table.
 *
 * The admins list needs two things from it that the user row does not carry.
 * The LABEL, because /users sends `role_name`, the identifier, so the Role
 * column would read "ceiling_test" where the roles page reads "Ceiling Test
 * (temporary)". And `canAct`, to decide whether that column is a link to the
 * role's page or plain text: the panel has no view-only role screen, so the
 * only place to send someone is the editor, and sending an operator there who
 * may not edit the role means a bounce back with a refusal.
 *
 * WHAT THIS USED TO BE. It also built every role's permission map, so the
 * panel could run its own strictly-below test and decide which admins were
 * actionable. GET /users answers that itself now — `canAct` per row — so the
 * maps and the test are gone. What is left is a lookup, not a rule.
 *
 * It is still a whole extra request. `role_label` and the role's `canAct` on
 * the admins row would end it; asked for, not blocking.
 */
const useRolesById = () => {
  const [roles, setRoles] = useState<RoleListRow[]>([]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const rows = await fetchRoles();
        if (!cancelled) setRoles(rows);
      } catch {
        // swallow — the column falls back to the identifier, unlinked
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(() => {
    const byId = new Map<number, RoleListRow>();

    roles.forEach(role => byId.set(role.id, role));

    return byId;
  }, [roles]);
};

export default useRolesById;
